/**
 * ManageCancelConfirm — the confirm step before a customer cancels their own
 * booking from the manage link.
 *
 * One tap on "Cancel booking" used to cancel outright, and the manage link
 * then answered "This link is no longer valid" — an accidental tap had no way
 * back. This dialog says what will happen first. Same shape as the product's
 * other destructive confirms (DeleteConfirmDialog): backdrop, centred card,
 * the safe choice first. The focus trap lands on "Keep my booking", so Enter
 * or Escape on arrival keeps the appointment; cancelling takes a deliberate
 * second tap.
 */
import React from 'react';

import { t } from '../../i18n';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface ManageCancelConfirmProps {
  open: boolean;
  /** A cancel is in flight — both buttons and the backdrop stop responding. */
  busy: boolean;
  onConfirm: () => void;
  onKeep: () => void;
}

export default function ManageCancelConfirm({
  open,
  busy,
  onConfirm,
  onKeep,
}: ManageCancelConfirmProps): React.ReactElement | null {
  const dialogRef = useFocusTrap(open, busy ? undefined : onKeep);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onKeep}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="manage-cancel-title"
          aria-describedby="manage-cancel-body"
          className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg"
          data-testid="manage-cancel-dialog"
        >
          <h2 id="manage-cancel-title" className="text-lg font-semibold text-gray-900">
            {t('manage.cancelConfirmTitle')}
          </h2>
          <p id="manage-cancel-body" className="mt-2 text-sm text-gray-600">
            {t('manage.cancelConfirmBody')}
          </p>
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onKeep}
              disabled={busy}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              data-testid="manage-cancel-keep"
            >
              {t('manage.cancelConfirmKeep')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
              data-testid="manage-cancel-confirm"
            >
              {busy ? t('manage.cancelling') : t('manage.cancelConfirmYes')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
